export default function EventStatus({
  text = '',
  status = '',
  size = 'small',
}) {
  // 尺寸配置
  const sizeConfig = {
    small: {
      container: 'gap-1 px-2 py-0.5 text-xs',
      icon: 'w-4 h-4',
    },
    default: {
      container: 'gap-2 px-4 py-2 text-sm',
      icon: 'w-5 h-5',
    },
  };

  const sizeStyle = sizeConfig[size] || sizeConfig.small;

  // 根据尺寸获取颜色配置（default 使用稍深的浅色背景）
  const getColorBySize = (
    lightBg,
    lightText,
    lightBorder,
    mediumBg,
    mediumText,
    mediumBorder
  ) => {
    if (size === 'default') {
      return {
        bg: mediumBg,
        text: mediumText,
        border: mediumBorder,
      };
    }
    return {
      bg: lightBg,
      text: lightText,
      border: lightBorder,
    };
  };

  // 状态配置：颜色、背景、图标、是否需要脉冲动画
  const statusConfig = {
    '-1': {
      color: getColorBySize(
        'bg-red-100',
        'text-red-700',
        'border-red-200',
        'bg-red-100',
        'text-red-700',
        'border-red-200'
      ),
      pulse: false,
      icon: (
        <svg className={sizeStyle.icon} fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    0: {
      color: getColorBySize(
        'bg-gray-100',
        'text-gray-700',
        'border-gray-200',
        'bg-gray-100',
        'text-gray-700',
        'border-gray-200'
      ),
      pulse: false,
      icon: (
        <svg className={sizeStyle.icon} fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    1: {
      color: getColorBySize(
        'bg-amber-100',
        'text-amber-700',
        'border-amber-200',
        'bg-amber-100',
        'text-amber-700',
        'border-amber-200'
      ),
      pulse: true,
      icon: (
        <svg className={sizeStyle.icon} fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    2: {
      color: getColorBySize(
        'bg-blue-100',
        'text-blue-700',
        'border-blue-200',
        'bg-blue-100',
        'text-blue-700',
        'border-blue-200'
      ),
      pulse: true,
      icon: (
        <svg className={sizeStyle.icon} fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    3: {
      color: getColorBySize(
        'bg-emerald-100',
        'text-emerald-700',
        'border-emerald-200',
        'bg-emerald-100',
        'text-emerald-700',
        'border-emerald-200'
      ),
      pulse: true,
      icon: (
        <svg className={sizeStyle.icon} fill="currentColor" viewBox="0 0 20 20">
          <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
        </svg>
      ),
    },
    4: {
      color: getColorBySize(
        'bg-cyan-100',
        'text-cyan-700',
        'border-cyan-200',
        'bg-cyan-100',
        'text-cyan-700',
        'border-cyan-200'
      ),
      pulse: true,
      icon: (
        <svg className={sizeStyle.icon} fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    5: {
      color: getColorBySize(
        'bg-purple-100',
        'text-purple-700',
        'border-purple-200',
        'bg-purple-100',
        'text-purple-700',
        'border-purple-200'
      ),
      pulse: true,
      icon: (
        <svg className={sizeStyle.icon} fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    6: {
      color: getColorBySize(
        'bg-slate-100',
        'text-slate-600',
        'border-slate-200',
        'bg-slate-100',
        'text-slate-600',
        'border-slate-200'
      ),
      pulse: false,
      icon: (
        <svg className={sizeStyle.icon} fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    7: {
      color: getColorBySize(
        'bg-green-100',
        'text-green-700',
        'border-green-200',
        'bg-green-100',
        'text-green-700',
        'border-green-200'
      ),
      pulse: false,
      icon: (
        <svg className={sizeStyle.icon} fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
  };

  const config = statusConfig[status] || {
    color: getColorBySize(
      'bg-gray-100',
      'text-gray-700',
      'border-gray-200',
      'bg-gray-100',
      'text-gray-700',
      'border-gray-200'
    ),
    pulse: false,
    icon: (
      <svg className={sizeStyle.icon} fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
          clipRule="evenodd"
        />
      </svg>
    ),
  };

  return (
    <span
      className={`inline-flex items-center rounded-full font-semibold border ${sizeStyle.container} ${config.color.bg} ${config.color.text} ${config.color.border}`}
    >
      {/* <span className={config.pulse ? 'animate-pulse' : ''}>{config.icon}</span> */}
      {text}
    </span>
  );
}
